import NinjaCommandConsole from '../components/NinjaCommandConsole';
import ActivityReview from '../components/ActivityReview';
import AssetIdentityHeader from '../components/AssetIdentityHeader';
import SoftwareInventory from '../components/SoftwareInventory';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recordHref } from '../utils/recordLinks';
import { htmlToText } from '../utils/htmlText';
import { RecordLink } from '../components/RecordLink';
import api from '../lib/api';
import bitdefenderService from '../services/bitdefenderService';
import screenconnectService from '../services/screenconnectService';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Package, Calendar, MapPin, ArrowLeft, Settings, 
  Ticket, RefreshCw, CheckCircle, XCircle, 
  AlertCircle, Activity, Shield, Monitor,
  HardDrive, Cpu, Network, Server, Power, Terminal,
  Download, Clock, AlertTriangle, Play, ExternalLink, PlayCircle, StopCircle,
  Trash2, Send, History, Bookmark, FolderOpen, Edit, Search, Plus, MonitorDot
} from 'lucide-react';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';
import NinjaDeviceWorkspace from '../components/NinjaDeviceWorkspace';
import TerminalEmulator from '../components/TerminalEmulator';

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [relatedTickets, setRelatedTickets] = useState([]);
  const [integrationData, setIntegrationData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    ninjarmm_device_id: '',
    bitdefender_endpoint_id: '',
    screenconnect_session_id: ''
  });

  // NinjaRMM deep data states
  const [ninjaData, setNinjaData] = useState({
    software: [],
    activities: [],
    alerts: [],
    networkInterfaces: [],
    processors: [],
    disks: [],
    services: [],
    dashboardUrl: ''
  });
  const [ninjaLoading, setNinjaLoading] = useState({
    software: false,
    activities: false,
    alerts: false,
    system: false,
    services: false
  });
  const [rebooting, setRebooting] = useState(false);
  const [bitdefenderData, setBitdefenderData] = useState(null);
  const [bitdefenderLoading, setBitdefenderLoading] = useState(false);
  const [screenconnectCreating, setScreenconnectCreating] = useState(false);
  const [managingService, setManagingService] = useState(null);

  // Remote command execution state
  const [cmdRunAs, setCmdRunAs] = useState('SYSTEM');
  const [cmdExecuting, setCmdExecuting] = useState(false);
  const [cmdResult, setCmdResult] = useState(null);
  const [cmdHistory, setCmdHistory] = useState([]);
  const [cmdHistoryLoading, setCmdHistoryLoading] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', script_type: 'POWERSHELL', script_content: '', run_as: 'SYSTEM', category: 'general', scope: 'global' });
  const [ninjaScripts, setNinjaScripts] = useState([]);
  const [ninjaScriptsLoading, setNinjaScriptsLoading] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptSearch, setScriptSearch] = useState('');
  const [scriptParams, setScriptParams] = useState('');
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [remoteSubTab, setRemoteSubTab] = useState('terminal');
  const [pollingHistoryId, setPollingHistoryId] = useState(null);
  const [pollingResult, setPollingResult] = useState(null);
  const pollingRef = React.useRef(null);

  useEffect(() => {
    loadAsset();
    loadRelatedTickets();
    loadBitdefenderData();
  }, [assetId]);

  const loadAsset = async () => {
    try {
      const response = await api.get(`/assets/${assetId}`);
      setAsset(response.data);
      setIntegrationData(response.data.integration_data || {});
      setEditForm({
        ninjarmm_device_id: response.data.ninjarmm_device_id || '',
        bitdefender_endpoint_id: response.data.bitdefender_endpoint_id || '',
        screenconnect_session_id: response.data.screenconnect_session_id || ''
      });
      
      // Load saved NinjaRMM data if available
      if (response.data.ninjarmm_data) {
        setNinjaData({
          software: response.data.ninjarmm_data.software || [],
          activities: response.data.ninjarmm_data.activities || [],
          alerts: response.data.ninjarmm_data.alerts || [],
          networkInterfaces: response.data.ninjarmm_data.networkInterfaces || [],
          processors: response.data.ninjarmm_data.processors || [],
          disks: response.data.ninjarmm_data.disks || [],
          services: response.data.ninjarmm_data.services || [],
          dashboardUrl: response.data.ninjarmm_data.dashboardUrl || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load asset');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedTickets = async () => {
    try {
      const response = await api.get(`/assets/${assetId}/tickets`);
      setRelatedTickets(response.data);
    } catch (error) {
      console.error('Failed to load related tickets');
    }
  };

  const loadBitdefenderData = async () => {
    if (!assetId) {
// removed console statement
      return;
    }
    
    setBitdefenderLoading(true);
    try {
// removed console statement
      const response = await bitdefenderService.getAssetEndpoint(assetId);
// removed console statement
      if (response && response.endpoint) {
        setBitdefenderData(response.endpoint);
// removed console statement
      } else {
        setBitdefenderData(null);
// removed console statement
      }
    } catch (error) {
      console.error('Failed to load BitDefender data:', error);
      setBitdefenderData(null);
    } finally {
      setBitdefenderLoading(false);
    }
  };


  const handleCreateScreenConnectSession = async () => {
    setScreenconnectCreating(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const result = await screenconnectService.createSession(
        asset.id,
        asset.name,
        user.full_name || user.email
      );
      
      if (result.success) {
        toast.success('Remote session created!');
        
        // Update asset state
        setAsset(prev => ({
          ...prev,
          screenconnect_session_id: result.session_id,
          screenconnect_session_status: 'active',
          screenconnect_guest_url: result.guest_url,
          screenconnect_host_url: result.host_url
        }));
        
        // Open the host URL automatically
        window.open(result.host_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create remote session');
    } finally {
      setScreenconnectCreating(false);
    }
  };



  // NinjaRMM data loading functions
  const loadNinjaSoftware = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, software: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/software`);
      const software = response.data.software || response.data || [];
      const softwareArray = Array.isArray(software) ? software : [];
      setNinjaData(prev => ({ ...prev, software: softwareArray }));
      
      // Save to database
      await api.post(`/assets/${assetId}/save-ninjarmm-data`, {
        software: softwareArray,
        last_updated: new Date().toISOString()
      });
      toast.success('Software inventory loaded and saved');
    } catch (error) {
      console.error('Failed to load software inventory:', error);
      toast.error('Failed to load software inventory');
      setNinjaData(prev => ({ ...prev, software: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, software: false }));
    }
  };

  const loadNinjaActivities = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, activities: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/activities`);
      const activitiesData = response.data.activities?.activities || response.data.activities || response.data || [];
      const activitiesArray = Array.isArray(activitiesData) ? activitiesData : [];
      setNinjaData(prev => ({ ...prev, activities: activitiesArray }));
      
      // Save to database
      const saved = await api.post(`/assets/${assetId}/save-ninjarmm-data`, {
        activities: activitiesArray,
        last_updated: new Date().toISOString()
      });
      setAsset(prev => prev?.id === assetId ? {...prev, ninjarmm_data: {...prev.ninjarmm_data, section_timestamps: saved.data.section_timestamps || prev.ninjarmm_data?.section_timestamps}} : prev);
      toast.success('Device activities loaded and saved');
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error('Failed to load device activities');
      setNinjaData(prev => ({ ...prev, activities: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const loadNinjaAlerts = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, alerts: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/alerts`);
      const alerts = response.data.alerts || response.data || [];
      const alertsArray = Array.isArray(alerts) ? alerts : [];
      setNinjaData(prev => ({ ...prev, alerts: alertsArray }));
      toast.success('Alerts loaded');
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error('Failed to load device alerts');
      setNinjaData(prev => ({ ...prev, alerts: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, alerts: false }));
    }
  };

  const loadNinjaSystemInfo = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, system: true }));
    try {
      const [networkRes, processorsRes, disksRes, dashboardRes] = await Promise.all([
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/network-interfaces`),
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/processors`),
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/disks`),
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/dashboard-url`)
      ]);
      
      const networkInterfaces = networkRes.data.interfaces || networkRes.data || [];
      const processors = processorsRes.data.processors || processorsRes.data || [];
      const disks = disksRes.data.disks || disksRes.data || [];
      
      const systemData = {
        networkInterfaces: Array.isArray(networkInterfaces) ? networkInterfaces : [],
        processors: Array.isArray(processors) ? processors : [],
        disks: Array.isArray(disks) ? disks : [],
        dashboardUrl: dashboardRes.data.url || ''
      };
      
      setNinjaData(prev => ({ ...prev, ...systemData }));
      
      // Save to database
      await api.post(`/assets/${assetId}/save-ninjarmm-data`, {
        ...systemData,
        last_updated: new Date().toISOString()
      });
      toast.success('System information loaded and saved');
    } catch (error) {
      console.error('Failed to load system info:', error);
      toast.error('Failed to load system information');
      setNinjaData(prev => ({
        ...prev,
        networkInterfaces: [],
        processors: [],
        disks: [],
        dashboardUrl: ''
      }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, system: false }));
    }
  };

  const loadNinjaServices = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, services: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/services`);
      const services = response.data.services || response.data || [];
      const servicesArray = Array.isArray(services) ? services : [];
      setNinjaData(prev => ({ ...prev, services: servicesArray }));
      
      // Save to database
      await api.post(`/assets/${assetId}/save-ninjarmm-data`, {
        services: servicesArray,
        last_updated: new Date().toISOString()
      });
      toast.success('Windows services loaded and saved');
    } catch (error) {
      console.error('Failed to load services:', error);
      toast.error('Failed to load Windows services');
      setNinjaData(prev => ({ ...prev, services: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, services: false }));
    }
  };

  const handleRebootDevice = async (mode = 'NORMAL') => {
    if (!asset?.ninjarmm_device_id) return;
    
    const confirmMsg = mode === 'SAFE' 
      ? 'Reboot device in Safe Mode?' 
      : mode === 'SAFE_WITH_NETWORKING'
      ? 'Reboot device in Safe Mode with Networking?'
      : 'Reboot device now?';
    
    if (!window.confirm(confirmMsg)) return;
    
    setRebooting(true);
    try {
      await api.post(`/ninjarmm/device/${asset.ninjarmm_device_id}/reboot?mode=${mode}`);
      toast.success(`Reboot command sent successfully (${mode} mode)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reboot command');
    } finally {
      setRebooting(false);
    }
  };


  const handleManageService = async (serviceName, action) => {
    if (!asset?.ninjarmm_device_id) return;
    
    const confirmMsg = `${action.charAt(0).toUpperCase() + action.slice(1)} the "${serviceName}" service?`;
    if (!window.confirm(confirmMsg)) return;
    
    setManagingService(`${serviceName}-${action}`);
    try {
      await api.post(`/ninjarmm/device/${asset.ninjarmm_device_id}/service/${encodeURIComponent(serviceName)}/manage?action=${action}`);
      toast.success(`Service ${action} command sent successfully`);
      
      // Reload services after a delay
      setTimeout(() => {
        loadNinjaServices();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} service`);
    } finally {
      setManagingService(null);
    }
  };


  const handleSyncIntegrations = async () => {
    setSyncing(true);
    try {
      const response = await api.post(`/assets/${assetId}/sync-integrations`);
      setIntegrationData(response.data.integration_data || {});
      setAsset(response.data);
      toast.success('Integration data synced');
    } catch (error) {
      toast.error('Failed to sync integrations');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadAssetPdf = async () => {
    try {
      const response = await api.get(`/export/assets/${assetId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset-${asset?.name || assetId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export PDF'); }
  };

  const handleRunSelectedScript = async () => {
    if (!selectedScript) { toast.error('Select a script from the NinjaRMM library first'); return; }
    if (!asset?.ninjarmm_device_id) { toast.error('No NinjaRMM device ID'); return; }
    setCmdExecuting(true);
    setCmdResult(null);
    setPollingResult(null);
    setPollingHistoryId(null);
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    try {
      const isAction = selectedScript.type === 'ACTION';
      const response = await api.post(`/ninja-remote/device/${asset.ninjarmm_device_id}/run`, {
        script_type: isAction ? 'ACTION' : 'SCRIPT',
        script_content: scriptParams || '',
        run_as: cmdRunAs,
        script_id: isAction ? undefined : selectedScript.id,
        script_uid: isAction ? selectedScript.uid : undefined,
        script_name: selectedScript.name || `Script #${selectedScript.id || selectedScript.uid}`,
      });
      setCmdResult(response.data);
      if (response.data.success) {
        toast.success(`Script "${selectedScript.name}" submitted — polling for results...`);
        loadCommandHistory();
        const historyId = response.data.history_id;
        if (historyId) {
          setPollingHistoryId(historyId);
          setPollingResult({ status: 'pending', message: 'Waiting for NinjaRMM to report results...' });
          let attempts = 0;
          pollingRef.current = setInterval(async () => {
            attempts++;
            try {
              const pollResp = await api.get(`/ninja-remote/history/${historyId}/poll-result`);
              const pollData = pollResp.data;
              setPollingResult(pollData);
              if (pollData.completed || attempts >= 30) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                setPollingHistoryId(null);
                loadCommandHistory();
                if (pollData.completed) {
                  toast[pollData.status === 'completed' ? 'success' : 'error'](`Script ${pollData.status}: ${selectedScript.name}`);
                } else {
                  toast.info('Polling timed out. Check history for results later.');
                }
              }
            } catch { /* continue polling */ }
          }, 5000);
        }
      } else {
        toast.error(response.data.message || response.data.error || 'Script execution failed');
        if (response.data.needs_auth) {
          toast.info('Go to Integrations > NinjaRMM > Connect to authorize script execution.', { duration: 8000 });
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to execute script';
      setCmdResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setCmdExecuting(false);
    }
  };

  React.useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const loadNinjaScripts = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaScriptsLoading(true);
    try {
      // Use device-specific scripts endpoint for relevant scripts
      const response = await api.get(`/ninja-remote/device/${asset.ninjarmm_device_id}/scripts`);
      const data = response.data;
      const scripts = data?.scripts || [];
      setNinjaScripts(scripts);
      setScriptsLoaded(true);
    } catch {
      // Fallback to global library
      try {
        const response = await api.get('/ninja-remote/scripts-library');
        setNinjaScripts(Array.isArray(response.data) ? response.data : []);
      } catch { setNinjaScripts([]); }
      setScriptsLoaded(true);
    }
    finally { setNinjaScriptsLoading(false); }
  };

  const loadCommandHistory = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setCmdHistoryLoading(true);
    try {
      const response = await api.get(`/ninja-remote/device/${asset.ninjarmm_device_id}/history?limit=20`);
      setCmdHistory(response.data);
    } catch { setCmdHistory([]); }
    finally { setCmdHistoryLoading(false); }
  };

  const handleDeleteHistoryEntry = async (entryId) => {
    try {
      await api.delete(`/ninja-remote/history/${entryId}`);
      setCmdHistory(prev => prev.filter(e => e.id !== entryId));
    } catch { toast.error('Failed to delete'); }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const custId = asset?.customer_id || '';
      const url = custId ? `/ninja-remote/templates?customer_id=${custId}` : '/ninja-remote/templates';
      const response = await api.get(url);
      setSavedTemplates(response.data);
    } catch { setSavedTemplates([]); }
    finally { setTemplatesLoading(false); }
  };

  const handleUseTemplate = async (tpl) => {
    setScriptParams(tpl.script_content);
    try { await api.post(`/ninja-remote/templates/${tpl.id}/use`); } catch {}
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) { toast.error('Name required'); return; }
    if (!templateForm.script_content.trim()) { toast.error('Script content required'); return; }
    try {
      if (editingTemplateId) {
        await api.put(`/ninja-remote/templates/${editingTemplateId}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/ninja-remote/templates', templateForm);
        toast.success('Template saved');
      }
      setShowTemplateForm(false);
      setEditingTemplateId(null);
      setTemplateForm({ name: '', description: '', script_type: 'POWERSHELL', script_content: '', run_as: 'SYSTEM', category: 'general', scope: 'global' });
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save template'); }
  };

  const handleDeleteTemplate = async (tplId) => {
    try {
      await api.delete(`/ninja-remote/templates/${tplId}`);
      setSavedTemplates(prev => prev.filter(t => t.id !== tplId));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveCurrentAsTemplate = () => {
    setTemplateForm({ name: '', description: '', script_type: selectedScript?.language || 'POWERSHELL', script_content: scriptParams, run_as: cmdRunAs, category: 'general', scope: 'global' });
    setEditingTemplateId(null);
    setShowTemplateForm(true);
  };

  const handleSaveIntegrationIds = async () => {
    try {
      await api.put(`/assets/${assetId}`, editForm);
      toast.success('Integration IDs updated');
      setEditMode(false);
      loadAsset();
    } catch (error) {
      toast.error('Failed to update integration IDs');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'maintenance':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'retired':
        return <XCircle className="h-5 w-5 text-gray-600" />;
      default:
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'retired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  const getIntegrationStatus = (integrationName) => {
    // Cached inventory cannot establish a current device link.
    if (integrationName === 'ninjarmm' && !asset?.ninjarmm_device_id) {
      return { status: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    }
    // Check if the integration has an ID configured
    if (integrationName === 'ninjarmm' && asset?.ninjarmm_device_id) {
      return { status: 'configured', label: 'Configured', color: 'bg-green-100 text-green-800' };
    }
    if (integrationName === 'bitdefender' && asset?.bitdefender_endpoint_id) {
      return { status: 'configured', label: 'Configured', color: 'bg-green-100 text-green-800' };
    }
    if (integrationName === 'screenconnect' && asset?.screenconnect_session_id) {
      return { status: 'configured', label: 'Configured', color: 'bg-green-100 text-green-800' };
    }
    
    // Fall back to integration_data if available
    const data = integrationData?.[integrationName];
    if (!data) return { status: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    
    switch (data.status) {
      case 'connected':
        return { status: 'connected', label: 'Connected', color: 'bg-green-100 text-green-800' };
      case 'ready':
        return { status: 'ready', label: 'Ready to Sync', color: 'bg-blue-100 text-blue-800' };
      case 'error':
        return { status: 'error', label: 'Error', color: 'bg-red-100 text-red-800' };
      default:
        return { status: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading asset details...</p>
        </div>
      </div>
    );
  }

  if (!asset) {
    return null;
  }

  return (
    <div className="space-y-6 pb-8" data-testid="asset-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 min-w-0">
          <Button variant="ghost" onClick={() => navigate('/assets')} data-testid="back-to-assets-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assets
          </Button>
          <Separator orientation="vertical" className="h-8" />
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-gray-600" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{asset.name}</h1>

            </div>
          </div>
          <Badge className={getStatusColor(asset.status)}>
            {getStatusIcon(asset.status)}
            <span className="ml-1 capitalize">{asset.status}</span>
          </Badge>
          <Button variant="outline" size="sm" onClick={handleDownloadAssetPdf} data-testid="export-asset-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      <AssetIdentityHeader key={asset.id} asset={asset} networkInterfaces={ninjaData.networkInterfaces} />

      <Tabs defaultValue="overview" className="space-y-4" onValueChange={(tab) => {
        if (tab === 'tools' && !scriptsLoaded && asset?.ninjarmm_device_id) {
          loadNinjaScripts();
          loadCommandHistory();
          loadTemplates();
        }
      }}>
        <div>
          <TabsList className="grid h-auto w-full grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-1">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
            <TabsTrigger value="software" disabled={!asset.ninjarmm_device_id}>Software</TabsTrigger>
            <TabsTrigger value="timeline" disabled={!asset.ninjarmm_device_id}>Timeline &amp; Remedy</TabsTrigger>
            <TabsTrigger value="activities" disabled={!asset.ninjarmm_device_id}>Activity</TabsTrigger>
            <TabsTrigger value="system" disabled={!asset.ninjarmm_device_id}>System Info</TabsTrigger>
            <TabsTrigger value="tickets">Tickets ({relatedTickets.length})</TabsTrigger>
            <TabsTrigger value="tools" disabled={!asset.ninjarmm_device_id}>Tools</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="timeline"><NinjaDeviceWorkspace assetId={assetId} /></TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Asset Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-500">Asset Name</Label>
                  <p className="font-semibold text-lg">{asset.name}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Type</Label>
                  <p className="capitalize">{asset.asset_type || 'Device'}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Serial Number</Label>
                  <p className="font-mono">{asset.serial_number || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Model</Label>
                  <p>{asset.model || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Manufacturer</Label>
                  <p>{asset.manufacturer || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Location & Ownership</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-500">Customer</Label>
                  <RecordLink
                    to={recordHref('customer', asset.customer_id, { tab: 'assets' })}
                    className="font-semibold text-blue-600 hover:underline block"
                    fallbackClassName="font-semibold block"
                    data-testid="asset-owner-link"
                  >
                    {asset.customer_name || 'N/A'}
                  </RecordLink>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Location</Label>
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {asset.location || 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Purchase Date</Label>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {asset.purchase_date ? formatInCST(asset.purchase_date, 'MMM d, yyyy') : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Warranty Expiry</Label>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {asset.warranty_expiry ? formatInCST(asset.warranty_expiry, 'MMM d, yyyy') : 'N/A'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {asset.notes && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap p-4 bg-gray-50 rounded border border-gray-200">
                    {asset.notes}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4">
          {/* Warning for assets with NinjaRMM but no BitDefender */}
          {asset.ninjarmm_device_id && !asset.bitdefender_endpoint_id && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-900">Security Alert: No Antivirus Protection</h4>
                <p className="text-sm text-red-800 mt-1">
                  This asset is monitored via NinjaRMM but has no BitDefender endpoint protection. 
                  Go to the BitDefender dashboard and sync endpoints to enable antivirus monitoring.
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => navigate('/bitdefender')}
                >
                  Go to BitDefender Dashboard
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Connect this asset to external monitoring and management systems
            </p>
            <div className="flex gap-2">
              {!editMode ? (
                <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Configure IDs
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveIntegrationIds}>
                    Save IDs
                  </Button>
                </>
              )}
              <Button size="sm" onClick={handleSyncIntegrations} disabled={syncing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* NinjaRMM */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    NinjaRMM
                  </CardTitle>
                  <Badge className={getIntegrationStatus('ninjarmm').color}>
                    {getIntegrationStatus('ninjarmm').label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <div>
                    <Label>Device ID</Label>
                    <Input
                      value={editForm.ninjarmm_device_id}
                      onChange={(e) => setEditForm({...editForm, ninjarmm_device_id: e.target.value})}
                      placeholder="NinjaRMM device ID"
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-gray-500">Device ID</Label>
                    <p className="font-mono text-sm">{asset.ninjarmm_device_id || 'Not configured'}</p>
                  </div>
                )}

                {integrationData?.ninjarmm?.status === 'connected' && integrationData?.ninjarmm?.data && (
                  <div className="space-y-3 p-3 bg-gray-50 rounded">
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <Badge className={integrationData.ninjarmm.data.online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                        {integrationData.ninjarmm.data.online ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">System Name</Label>
                      <p className="text-sm">{integrationData.ninjarmm.data.system_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">OS</Label>
                      <p className="text-sm">{integrationData.ninjarmm.data.os_name || 'N/A'}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BitDefender */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    BitDefender
                  </CardTitle>
                  <Badge className={getIntegrationStatus('bitdefender').color}>
                    {getIntegrationStatus('bitdefender').label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <div>
                    <Label>Endpoint ID</Label>
                    <Input
                      value={editForm.bitdefender_endpoint_id}
                      onChange={(e) => setEditForm({...editForm, bitdefender_endpoint_id: e.target.value})}
                      placeholder="Auto-synced from BitDefender"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Endpoint ID is auto-linked during BitDefender sync</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-gray-500">Endpoint ID</Label>
                    <p className="font-mono text-sm">{asset.bitdefender_endpoint_id || 'Not linked'}</p>
                  </div>
                )}

                {bitdefenderLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Activity className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : bitdefenderData ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded">
                      <div>
                        <Label className="text-xs text-gray-500">Security Status</Label>
                        <Badge className={
                          bitdefenderData.security_status === 'protected' ? 'bg-green-100 text-green-800' :
                          bitdefenderData.security_status === 'at_risk' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {bitdefenderData.security_status?.toUpperCase()}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Antimalware</Label>
                        <Badge variant={bitdefenderData.antimalware_enabled ? 'default' : 'destructive'}>
                          {bitdefenderData.antimalware_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Product Status</Label>
                        <Badge variant={bitdefenderData.product_outdated ? 'destructive' : 'default'}>
                          {bitdefenderData.product_outdated ? 'Outdated' : 'Current'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Threats</Label>
                        <p className="text-sm font-semibold text-green-600">{bitdefenderData.threat_count || 0}</p>
                      </div>
                    </div>

                    {bitdefenderData.last_scan_date && (
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <Label className="text-xs text-gray-600">Last Scan</Label>
                        <p className="text-sm">{formatInCST(bitdefenderData.last_scan_date, 'MMM d, yyyy h:mm a')}</p>
                      </div>
                    )}
                  </div>
                ) : asset.bitdefender_endpoint_id ? (
                  <p className="text-sm text-gray-500 italic">
                    BitDefender data not available. Try syncing from the BitDefender dashboard.
                  </p>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 italic mb-3">
                      No BitDefender endpoint linked. Sync endpoints from BitDefender dashboard to auto-link.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate('/bitdefender')}
                    >
                      Go to BitDefender Dashboard
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ScreenConnect */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    ScreenConnect
                  </CardTitle>
                  <Badge className={getIntegrationStatus('screenconnect').color}>
                    {getIntegrationStatus('screenconnect').label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <div>
                    <Label>Active Session ID</Label>
                    <Input
                      value={editForm.screenconnect_session_id}
                      onChange={(e) => setEditForm({...editForm, screenconnect_session_id: e.target.value})}
                      placeholder="Session created automatically"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Sessions are created automatically when you launch remote support</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-gray-500">Active Session ID</Label>
                    <p className="font-mono text-sm">{asset.screenconnect_session_id || 'No active session'}</p>
                    {asset.screenconnect_session_status && (
                      <Badge className="mt-1 bg-green-100 text-green-800">
                        {asset.screenconnect_session_status.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                )}

                {asset.screenconnect_session_id && asset.screenconnect_session_status === 'active' ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border border-green-200 rounded">
                      <Label className="text-xs text-green-900 font-semibold">Active Remote Session</Label>
                      <p className="text-sm text-green-800 mt-1">A remote session is currently active for this asset</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="default"
                        size="sm"
                        onClick={() => {
                          window.open(asset.screenconnect_host_url || screenconnectService.getHostJoinUrl(asset.screenconnect_session_id), '_blank', 'noopener,noreferrer');
                          toast.info('Opening ScreenConnect session...');
                        }}
                        data-testid="join-session-btn"
                      >
                        <Monitor className="h-4 w-4 mr-2" />
                        Join Session
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const url = asset.screenconnect_guest_url || screenconnectService.getGuestJoinUrl(asset.screenconnect_session_id);
                          
                          // Fallback copy method for restricted environments
                          const textarea = document.createElement('textarea');
                          textarea.value = url;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          
                          try {
                            document.execCommand('copy');
                            toast.success('Guest link copied to clipboard');
                          } catch (err) {
                            toast.error('Copy failed. Link: ' + url);
                          }
                          
                          document.body.removeChild(textarea);
                        }}
                      >
                        Copy Guest Link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Create a new remote support session to access this asset remotely
                    </p>
                    <Button 
                      onClick={handleCreateScreenConnectSession}
                      disabled={screenconnectCreating}
                      className="w-full"
                      data-testid="create-screenconnect-session-btn"
                    >
                      {screenconnectCreating ? (
                        <>
                          <Activity className="h-4 w-4 mr-2 animate-spin" />
                          Creating Session...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Launch Remote Support
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 italic">
                      Creates a new ScreenConnect session and opens the remote control window
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {asset.last_integration_sync && (
            <p className="text-xs text-gray-500 text-center mt-4">
              Last synced: {formatInCST(asset.last_integration_sync, 'MMM d, yyyy h:mm a')}
            </p>
          )}
        </TabsContent>

        {/* Software Tab - Same as dialog */}
        <TabsContent value="software">
          {!asset.ninjarmm_device_id ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Download className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No NinjaRMM device configured</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Installed Software
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={loadNinjaSoftware} 
                    disabled={ninjaLoading.software}
                    data-testid="load-software-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.software ? 'animate-spin' : ''}`} />
                    {ninjaLoading.software ? 'Loading...' : 'Load Software'}
                  </Button>
                </div>
                {asset.ninjarmm_last_sync && (
                  <p className="text-xs text-gray-500 mt-2">
                    Last synced: {formatInCST(asset.ninjarmm_last_sync, 'MMM d, yyyy h:mm a')}
                    {asset.ninjarmm_last_sync_by && ` by ${asset.ninjarmm_last_sync_by_name || asset.ninjarmm_last_sync_by}`}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {ninjaLoading.software ? (
                  <p className="text-center py-8 text-gray-500">Loading software inventory...</p>
                ) : ninjaData.software.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Download className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No software data loaded yet</p>
                    <p className="text-sm mt-1">Click "Load Software" to fetch installed applications</p>
                  </div>
                ) : (
                  <SoftwareInventory key={asset.id} software={ninjaData.software} />
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activity Log Tab - Same as dialog */}
        <TabsContent value="activities">
          {!asset.ninjarmm_device_id ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No NinjaRMM device configured</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Device Activity Log
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={loadNinjaActivities} 
                    disabled={ninjaLoading.activities}
                    data-testid="load-activities-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.activities ? 'animate-spin' : ''}`} />
                    {ninjaLoading.activities ? 'Loading...' : 'Load Activities'}
                  </Button>
                </div>
                {asset.ninjarmm_data?.section_timestamps?.activities && (
                  <p className="text-xs text-muted-foreground mt-2">Activity cache refreshed: {formatInCST(asset.ninjarmm_data.section_timestamps.activities, 'MMM d, yyyy h:mm a')}</p>
                )}
              </CardHeader>
              <CardContent>
                {ninjaLoading.activities ? <p className="py-8 text-muted-foreground">Loading activities…</p> : <ActivityReview activities={ninjaData.activities} />}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* System Info Tab - Same as dialog */}
        <TabsContent value="system">
          {!asset.ninjarmm_device_id ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Server className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No NinjaRMM device configured</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  {asset.ninjarmm_last_sync && (
                    <p className="text-xs text-gray-500">
                      Last synced: {formatInCST(asset.ninjarmm_last_sync, 'MMM d, yyyy h:mm a')}
                      {asset.ninjarmm_last_sync_by && ` by ${asset.ninjarmm_last_sync_by_name || asset.ninjarmm_last_sync_by}`}
                    </p>
                  )}
                </div>
                <Button 
                  size="sm" 
                  onClick={loadNinjaSystemInfo} 
                  disabled={ninjaLoading.system}
                  data-testid="load-system-info-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.system ? 'animate-spin' : ''}`} />
                  {ninjaLoading.system ? 'Loading...' : 'Load System Info'}
                </Button>
              </div>

              {/* Processors */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Cpu className="h-5 w-5" />
                    Processors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.system ? (
                    <p className="text-center py-4 text-gray-500">Loading...</p>
                  ) : ninjaData.processors.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No processor data available</p>
                  ) : (
                    <div className="space-y-3">
                      {ninjaData.processors.map((proc, idx) => (
                        <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-base text-gray-900">{proc.name || 'Unknown Processor'}</h4>
                              {proc.architecture && (
                                <Badge className="mt-1 bg-blue-100 text-blue-800 text-xs">
                                  {proc.architecture}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Physical Cores</Label>
                              <p className="font-medium text-gray-900">{proc.numCores || proc.cores || proc.numberOfCores || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Logical Cores</Label>
                              <p className="font-medium text-gray-900">{proc.numLogicalCores || proc.threads || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Base Clock</Label>
                              <p className="font-medium text-gray-900">
                                {proc.clockSpeed 
                                  ? `${(proc.clockSpeed / 1000000000).toFixed(2)} GHz` 
                                  : proc.speed 
                                  ? `${proc.speed} MHz`
                                  : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Max Clock</Label>
                              <p className="font-medium text-gray-900">
                                {proc.maxClockSpeed 
                                  ? `${(proc.maxClockSpeed / 1000000000).toFixed(2)} GHz`
                                  : 'N/A'}
                              </p>
                            </div>
                            {proc.manufacturer && (
                              <div className="col-span-2">
                                <Label className="text-xs text-gray-500 font-semibold">Manufacturer</Label>
                                <p className="text-sm text-gray-700">{proc.manufacturer}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Disks */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <HardDrive className="h-5 w-5" />
                    Disk Drives
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.system ? (
                    <p className="text-center py-4 text-gray-500">Loading...</p>
                  ) : ninjaData.disks.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No disk data available</p>
                  ) : (
                    <div className="space-y-3">
                      {(Array.isArray(ninjaData.disks) ? ninjaData.disks : []).map((disk, idx) => (
                        <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-base text-gray-900">{disk.name || disk.drive || 'Unknown Drive'}</h4>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {disk.description && (
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">{disk.description}</Badge>
                                )}
                                {disk.mediaType && (
                                  <Badge className="bg-gray-100 text-gray-800 text-xs">{disk.mediaType}</Badge>
                                )}
                                {disk.status && (
                                  <Badge className={disk.status === 'OK' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                    {disk.status}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-3">
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Total Capacity</Label>
                              <p className="font-medium text-gray-900">{disk.size ? `${(disk.size / 1024 / 1024 / 1024).toFixed(2)} GB` : 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Free Space</Label>
                              <p className="font-medium text-gray-900">{disk.freeSpace ? `${(disk.freeSpace / 1024 / 1024 / 1024).toFixed(2)} GB` : 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Model</Label>
                              <p className="font-medium text-gray-900">{disk.model || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500 font-semibold">Interface</Label>
                              <p className="font-medium text-gray-900">{disk.interfaceType || 'N/A'}</p>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500 font-semibold">Serial Number</Label>
                              <p className="font-mono text-xs text-gray-700">{disk.serialNumber || 'N/A'}</p>
                            </div>
                            {disk.partitionCount && (
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Partitions</Label>
                                <p className="font-medium text-gray-900">{disk.partitionCount}</p>
                              </div>
                            )}
                            {disk.bytesPerSector && (
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Sector Size</Label>
                                <p className="font-medium text-gray-900">{disk.bytesPerSector} bytes</p>
                              </div>
                            )}
                          </div>
                          
                          {disk.size && disk.freeSpace && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Disk Usage</span>
                                <span className="font-semibold">
                                  {((disk.size - disk.freeSpace) / 1024 / 1024 / 1024).toFixed(2)} GB used / {(disk.size / 1024 / 1024 / 1024).toFixed(2)} GB total
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-3">
                                  <div 
                                    className={`h-3 rounded-full transition-all ${
                                      ((disk.size - disk.freeSpace) / disk.size * 100) > 90 ? 'bg-red-600' :
                                      ((disk.size - disk.freeSpace) / disk.size * 100) > 75 ? 'bg-orange-500' :
                                      'bg-blue-600'
                                    }`}
                                    style={{ width: `${((disk.size - disk.freeSpace) / disk.size * 100).toFixed(1)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold text-gray-700 min-w-[50px] text-right">
                                  {((disk.size - disk.freeSpace) / disk.size * 100).toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Network Interfaces */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Network className="h-5 w-5" />
                    Network Interfaces
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.system ? (
                    <p className="text-center py-4 text-gray-500">Loading...</p>
                  ) : ninjaData.networkInterfaces.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No network interface data available</p>
                  ) : (
                    <div className="space-y-3">
                      {(Array.isArray(ninjaData.networkInterfaces) ? ninjaData.networkInterfaces : []).map((iface, idx) => {
                        // Separate IPv4 and IPv6 addresses
                        const ipAddresses = Array.isArray(iface.ipAddress) ? iface.ipAddress : 
                                          (iface.ipAddress ? [iface.ipAddress] : []);
                        const ipv4Addresses = ipAddresses.filter(ip => !ip.includes(':'));
                        const ipv6Addresses = ipAddresses.filter(ip => ip.includes(':'));
                        
                        return (
                          <div key={idx} className="p-3 bg-gray-50 rounded border">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="col-span-2">
                                <Label className="text-xs text-gray-500">Interface Name</Label>
                                <p className="font-medium">{iface.interfaceName || iface.name || iface.description || 'N/A'}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">MAC Address</Label>
                                <p className="font-mono text-xs">
                                  {Array.isArray(iface.macAddress) ? iface.macAddress[0] : (iface.macAddress || iface.mac || 'N/A')}
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Status</Label>
                                <Badge className={
                                  iface.status === 'Up' || iface.status === 'Connected' || iface.enabled
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }>
                                  {iface.status || (iface.enabled ? 'Active' : 'Inactive')}
                                </Badge>
                              </div>
                              
                              {ipv4Addresses.length > 0 && (
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500 flex items-center gap-1">
                                    <Network className="h-3 w-3" />
                                    IPv4 Address{ipv4Addresses.length > 1 ? 'es' : ''}
                                  </Label>
                                  <div className="space-y-1">
                                    {ipv4Addresses.map((ip, ipIdx) => (
                                      <p key={ipIdx} className="font-mono text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
                                        {ip}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {ipv6Addresses.length > 0 && (
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500 flex items-center gap-1">
                                    <Network className="h-3 w-3" />
                                    IPv6 Address{ipv6Addresses.length > 1 ? 'es' : ''}
                                  </Label>
                                  <div className="space-y-1">
                                    {ipv6Addresses.map((ip, ipIdx) => (
                                      <p key={ipIdx} className="font-mono text-xs bg-purple-50 px-2 py-1 rounded border border-purple-200 break-all">
                                        {ip}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {iface.interfaceType && (
                                <div>
                                  <Label className="text-xs text-gray-500">Type</Label>
                                  <p className="text-xs">{iface.interfaceType}</p>
                                </div>
                              )}
                              {iface.linkSpeed && (
                                <div>
                                  <Label className="text-xs text-gray-500">Link Speed</Label>
                                  <p className="text-xs">{(parseInt(iface.linkSpeed) / 1000000).toFixed(0)} Mbps</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Alerts */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Active Alerts
                    </CardTitle>
                    <Button 
                      size="sm" 
                      onClick={loadNinjaAlerts} 
                      disabled={ninjaLoading.alerts}
                      data-testid="load-alerts-btn"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.alerts ? 'animate-spin' : ''}`} />
                      {ninjaLoading.alerts ? 'Loading...' : 'Refresh Alerts'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.alerts ? (
                    <p className="text-center py-4 text-gray-500">Loading alerts...</p>
                  ) : ninjaData.alerts.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
                      <p className="text-sm">No active alerts</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(Array.isArray(ninjaData.alerts) ? ninjaData.alerts : []).map((alert, idx) => (
                        <Card key={idx} className="border-l-4 border-l-red-500">
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-sm">{alert.message || alert.title || 'Alert'}</p>
                                <p className="text-xs text-gray-600 mt-1">{alert.description || 'No description'}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {alert.createTime ? formatInCST(alert.createTime, 'MMM d, yyyy h:mm a') : 'Unknown time'}
                                </p>
                              </div>
                              {alert.priority && (
                                <Badge className={
                                  alert.priority === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                                  alert.priority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }>
                                  {alert.priority}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Tools Tab - Same as dialog */}
        <TabsContent value="tools">
          {asset.ninjarmm_device_id && <div className="mb-6"><NinjaCommandConsole assetId={assetId} /></div>}
          {!asset.ninjarmm_device_id ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Terminal className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No NinjaRMM device configured</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Dashboard Link & Remote Access */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    Remote Access & Management
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">NinjaRMM Dashboard</Label>
                    <p className="text-sm text-gray-600 mb-3">
                      Open this device in NinjaRMM for remote control, advanced diagnostics, and management tools
                    </p>
                    <Button 
                      onClick={async () => {
                        const deviceId = asset.ninjarmm_device_id;
                        const dashboardUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview`;
                        window.open(dashboardUrl, '_blank');
                      }}
                      className="w-full sm:w-auto"
                      data-testid="open-dashboard-btn"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in NinjaRMM Dashboard
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">Quick Remote Actions</Label>
                    <p className="text-sm text-gray-600 mb-3">
                      Open device dashboard in NinjaRMM to access remote tools
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const deviceId = asset.ninjarmm_device_id;
                          const dashboardUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview`;
                          window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
                          toast.info('Opening device dashboard - click Remote Desktop from the Actions menu');
                        }}
                        data-testid="dashboard-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Dashboard
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const deviceId = asset.ninjarmm_device_id;
                          const remoteUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview?action=remote`;
                          window.open(remoteUrl, '_blank', 'noopener,noreferrer');
                          toast.info('Opening dashboard - Click "Remote Desktop" button in the Actions panel');
                        }}
                        data-testid="remote-control-btn"
                      >
                        <Monitor className="h-4 w-4 mr-2" />
                        Remote
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const deviceId = asset.ninjarmm_device_id;
                          const terminalUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview?action=terminal`;
                          window.open(terminalUrl, '_blank', 'noopener,noreferrer');
                          toast.info('Opening dashboard - Click "Terminal" button in the Actions panel');
                        }}
                        data-testid="terminal-btn"
                      >
                        <Terminal className="h-4 w-4 mr-2" />
                        Terminal
                      </Button>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-xs text-blue-900 font-semibold mb-1">📌 How to access remote tools:</p>
                      <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
                        <li>Button opens the device dashboard in NinjaRMM</li>
                        <li>Look for the <strong>Actions</strong> panel on the right side</li>
                        <li>Click <strong>"Remote Desktop"</strong> or <strong>"Terminal"</strong> button</li>
                        <li>Remote session will launch in a new window</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Reboot Controls */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Power className="h-5 w-5" />
                    Reboot Device
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Remotely restart this device. Use Safe Mode options if normal boot fails.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline"
                      onClick={() => handleRebootDevice('NORMAL')} 
                      disabled={rebooting}
                      data-testid="reboot-normal-btn"
                    >
                      <Power className="h-4 w-4 mr-2" />
                      Normal Reboot
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleRebootDevice('SAFE')} 
                      disabled={rebooting}
                      data-testid="reboot-safe-btn"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Safe Mode
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleRebootDevice('SAFE_WITH_NETWORKING')} 
                      disabled={rebooting}
                      data-testid="reboot-safe-network-btn"
                    >
                      <Network className="h-4 w-4 mr-2" />
                      Safe Mode + Network
                    </Button>
                  </div>
                  {rebooting && (
                    <p className="text-sm text-blue-600 mt-3">Sending reboot command...</p>
                  )}
                </CardContent>
              </Card>

              {/* Windows Services */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Play className="h-5 w-5" />
                      Windows Services
                    </CardTitle>
                    <Button 
                      size="sm" 
                      onClick={loadNinjaServices} 
                      disabled={ninjaLoading.services}
                      data-testid="load-services-btn"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.services ? 'animate-spin' : ''}`} />
                      {ninjaLoading.services ? 'Loading...' : 'Load Services'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.services ? (
                    <p className="text-center py-4 text-gray-500">Loading services...</p>
                  ) : ninjaData.services.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <Play className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm">No services data loaded</p>
                      <p className="text-xs mt-1">Click "Load Services" to view Windows services</p>
                    </div>
                  ) : (
                    <div className="max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Service Name</TableHead>
                            <TableHead>Display Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Startup Type</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(Array.isArray(ninjaData.services) ? ninjaData.services : []).map((service, idx) => {
                            const serviceName = service.name || '';
                            const isRunning = service.status === 'Running' || service.state === 'Running';
                            const isManaging = managingService?.startsWith(serviceName);
                            
                            return (
                              <TableRow key={idx}>
                                <TableCell className="font-mono text-xs">{serviceName || 'N/A'}</TableCell>
                                <TableCell className="text-sm">{service.displayName || service.description || 'N/A'}</TableCell>
                                <TableCell>
                                  <Badge 
                                    variant="custom"
                                    className={
                                      isRunning
                                        ? 'bg-green-100 text-green-800 hover:bg-green-200 transition-colors' 
                                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors'
                                    }
                                  >
                                    {service.status || service.state || 'Unknown'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm capitalize">
                                  {service.startupType || service.startMode || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleManageService(serviceName, 'start')}
                                      disabled={isRunning || isManaging}
                                      className="h-7 px-2 text-xs"
                                      title="Start service"
                                    >
                                      <PlayCircle className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleManageService(serviceName, 'stop')}
                                      disabled={!isRunning || isManaging}
                                      className="h-7 px-2 text-xs"
                                      title="Stop service"
                                    >
                                      <StopCircle className="h-3 w-3" />
                                      </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleManageService(serviceName, 'restart')}
                                      disabled={!isRunning || isManaging}
                                      className="h-7 px-2 text-xs"
                                      title="Restart service"
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ninja script library */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Terminal className="h-5 w-5" />
                      Ninja script library
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={loadNinjaScripts} disabled={ninjaScriptsLoading} data-testid="refresh-scripts-btn">
                        <RefreshCw className={`h-4 w-4 mr-1 ${ninjaScriptsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                      <Button size="sm" variant="outline" onClick={loadCommandHistory} disabled={cmdHistoryLoading} data-testid="load-cmd-history-btn">
                        <History className={`h-4 w-4 mr-1 ${cmdHistoryLoading ? 'animate-spin' : ''}`} />
                        History
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Sub-tab navigation */}
                  <div className="flex gap-1 border-b pb-2">
                    {[
                      { key: 'terminal', label: 'Script parameters', icon: MonitorDot, risk: 'high' },
                      { key: 'scripts', label: 'NinjaRMM Scripts', icon: ExternalLink, risk: 'standard' },
                      { key: 'templates', label: 'Templates', icon: FolderOpen, risk: 'standard' },
                      { key: 'history', label: 'History', icon: History },
                    ].map(({ key, label, icon: Icon, risk }) => (
                      <button
                        key={key}
                        onClick={() => setRemoteSubTab(key)}
                        className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors ${
                          remoteSubTab === key
                            ? (risk === 'high' ? 'bg-amber-800 text-white' : 'bg-gray-900 text-white')
                            : (risk === 'high' ? 'text-amber-700 hover:bg-amber-50' : 'text-gray-600 hover:bg-gray-100')
                        }`}
                        data-testid={`remote-tab-${key}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                        {key === 'terminal' && <span className="text-[9px] uppercase font-bold opacity-70">Admin</span>}
                        {key === 'scripts' && ninjaScripts.length > 0 && <span className="text-[10px] ml-0.5 opacity-70">({ninjaScripts.length})</span>}
                        {key === 'templates' && savedTemplates.length > 0 && <span className="text-[10px] ml-0.5 opacity-70">({savedTemplates.length})</span>}
                        {key === 'history' && cmdHistory.length > 0 && <span className="text-[10px] ml-0.5 opacity-70">({cmdHistory.length})</span>}
                      </button>
                    ))}
                  </div>

                  {/* Terminal Tab — HIGH RISK COMMAND EXECUTION */}
                  {remoteSubTab === 'terminal' && (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="command-risk-warning">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Administrative script parameters</p>
                          <p className="text-xs text-amber-700 mt-0.5">Input here is passed as parameters to the selected Ninja script. Use PowerShell / CMD jobs above for commands. Approval may be required.</p>
                        </div>
                      </div>
                      <TerminalEmulator
                        deviceId={asset.ninjarmm_device_id}
                        assetName={asset.name}
                        osType={asset.os_type || integrationData?.os?.name || 'windows'}
                        scripts={ninjaScripts}
                        runAs={cmdRunAs}
                        onSessionLog={(entries) => {
                          // Auto-save session when it has entries
                          if (entries.length > 0 && entries.length % 5 === 0) {
                            api.post('/ninja-remote/terminal-session', {
                              device_id: asset.ninjarmm_device_id,
                              asset_name: asset.name,
                              entries: entries.slice(-5),
                            }).catch(() => {});
                          }
                        }}
                      />
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>Run as: <strong>{cmdRunAs}</strong></span>
                        <Select value={cmdRunAs} onValueChange={setCmdRunAs}>
                          <SelectTrigger className="w-36 h-7 text-xs" data-testid="terminal-runas-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SYSTEM">System</SelectItem>
                            <SelectItem value="LOGGED_ON_USER">Logged-on User</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-gray-400">|</span>
                        <span>Sessions are logged for audit</span>
                      </div>
                    </div>
                  )}

                  {/* Scripts Tab */}
                  {remoteSubTab === 'scripts' && (
                    <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 flex items-start gap-2" data-testid="scripts-info-banner">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">NinjaRMM requires scripts from its library</p>
                          <p className="mt-0.5 opacity-80">Select a script below to run on this device. To add custom scripts, create them in NinjaRMM under Administration &gt; Library &gt; Automation.</p>
                        </div>
                      </div>

                      {/* Run As selector */}
                      <div className="flex items-center gap-3">
                        <Label className="text-xs text-gray-500 whitespace-nowrap">Run As:</Label>
                        <Select value={cmdRunAs} onValueChange={setCmdRunAs}>
                          <SelectTrigger className="w-48 h-8 text-sm" data-testid="cmd-runas-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SYSTEM">System</SelectItem>
                            <SelectItem value="LOGGED_ON_USER">Logged-on User</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Script search */}
                      <div className="relative">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          value={scriptSearch}
                          onChange={(e) => setScriptSearch(e.target.value)}
                          placeholder="Search scripts..."
                          className="pl-9 h-9"
                          data-testid="script-search-input"
                        />
                      </div>

                      {ninjaScriptsLoading ? (
                        <div className="flex items-center justify-center py-8 text-gray-500">
                          <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                          Loading scripts from NinjaRMM...
                        </div>
                      ) : ninjaScripts.length === 0 ? (
                        <div className="text-center py-8 text-gray-500" data-testid="no-scripts-msg">
                          <Terminal className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm font-medium">No scripts found in NinjaRMM</p>
                          <p className="text-xs mt-1">Create scripts in NinjaRMM under Administration &gt; Library &gt; Automation</p>
                        </div>
                      ) : (
                        <div className="max-h-[320px] overflow-y-auto space-y-1.5 border rounded-lg p-2" data-testid="ninja-scripts-list">
                          {ninjaScripts
                            .filter(s => !scriptSearch || (s.name || '').toLowerCase().includes(scriptSearch.toLowerCase()) || (s.language || '').toLowerCase().includes(scriptSearch.toLowerCase()))
                            .map((script) => {
                            const scriptKey = script.uid || script.id;
                            const selectedKey = selectedScript?.uid || selectedScript?.id;
                            const isSelected = selectedKey && scriptKey && selectedKey === scriptKey;
                            return (
                            <div
                              key={scriptKey}
                              onClick={() => { setSelectedScript(script); setScriptParams(''); setCmdResult(null); }}
                              className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all ${
                                isSelected
                                  ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' 
                                  : 'bg-white hover:bg-gray-50 border-gray-200'
                              }`}
                              data-testid={`ninja-script-${scriptKey}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium truncate">{script.name || `Script #${script.id}`}</span>
                                  {script.type === 'ACTION' && <Badge variant="outline" className="text-[10px] shrink-0 bg-purple-50 text-purple-700 border-purple-200">Action</Badge>}
                                  {script.language && <Badge variant="outline" className="text-[10px] shrink-0">{script.language}</Badge>}
                                </div>
                                {script.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{script.description}</p>}
                              </div>
                              {isSelected && (
                                <CheckCircle className="h-4 w-4 text-blue-500 shrink-0 ml-2" />
                              )}
                            </div>
                          );})}
                        </div>
                      )}

                      {/* Selected script execution panel */}
                      {selectedScript && (
                        <div className="bg-gray-50 rounded-lg border p-3 space-y-3" data-testid="selected-script-panel">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <PlayCircle className="h-5 w-5 text-blue-500" />
                              <span className="text-sm font-semibold">{selectedScript.name}</span>
                              {selectedScript.language && <Badge variant="outline" className="text-[10px]">{selectedScript.language}</Badge>}
                            </div>
                            <button onClick={() => { setSelectedScript(null); setScriptParams(''); }} className="text-gray-400 hover:text-gray-600">
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Script Parameters (optional)</Label>
                            <Textarea
                              value={scriptParams}
                              onChange={(e) => setScriptParams(e.target.value)}
                              placeholder="Enter parameters to pass to the script..."
                              rows={2}
                              className="font-mono text-sm"
                              data-testid="script-params-input"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Button onClick={handleRunSelectedScript} disabled={cmdExecuting} data-testid="execute-script-btn">
                              <Play className="h-4 w-4 mr-2" />
                              {cmdExecuting ? 'Executing...' : 'Execute on Device'}
                            </Button>
                            {scriptParams.trim() && (
                              <Button size="sm" variant="outline" onClick={handleSaveCurrentAsTemplate} data-testid="save-as-template-btn">
                                <Bookmark className="h-4 w-4 mr-1" />
                                Save as Template
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Command Result */}
                      {cmdResult && (
                        <div className={`p-3 rounded-lg border ${cmdResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="cmd-result">
                          <p className={`text-sm font-semibold ${cmdResult.success ? 'text-green-800' : 'text-red-800'}`}>
                            {cmdResult.success ? 'Command Submitted' : 'Command Failed'}
                          </p>
                          <p className="text-xs mt-1 text-gray-600">{cmdResult.message || cmdResult.error}</p>
                          {cmdResult.job_id && (
                            <p className="text-xs mt-1 font-mono text-gray-500">Job ID: {cmdResult.job_id}</p>
                          )}
                        </div>
                      )}

                      {/* Polling Result Viewer */}
                      {pollingResult && (
                        <div className={`rounded-lg border overflow-hidden ${
                          pollingResult.completed 
                            ? (pollingResult.status === 'completed' ? 'border-green-300' : 'border-red-300')
                            : 'border-blue-300'
                        }`} data-testid="polling-result">
                          <div className={`px-3 py-2 flex items-center justify-between text-sm font-medium ${
                            pollingResult.completed
                              ? (pollingResult.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            <div className="flex items-center gap-2">
                              {!pollingResult.completed && (
                                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                              )}
                              {pollingResult.completed && pollingResult.status === 'completed' && (
                                <CheckCircle className="h-3.5 w-3.5" />
                              )}
                              {pollingResult.completed && pollingResult.status === 'failed' && (
                                <XCircle className="h-3.5 w-3.5" />
                              )}
                              <span>
                                {pollingResult.status === 'pending' && 'Waiting for results...'}
                                {pollingResult.status === 'running' && 'Script is running...'}
                                {pollingResult.status === 'completed' && 'Script Completed Successfully'}
                                {pollingResult.status === 'failed' && 'Script Failed'}
                                {pollingResult.status === 'error' && 'Error checking status'}
                              </span>
                            </div>
                            {pollingHistoryId && !pollingResult.completed && (
                              <button
                                onClick={() => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } setPollingHistoryId(null); }}
                                className="text-xs underline opacity-70 hover:opacity-100"
                              >
                                Stop polling
                              </button>
                            )}
                          </div>
                          {pollingResult.output && (
                            <div className="bg-gray-900 text-green-400 p-3 font-mono text-xs max-h-[300px] overflow-y-auto whitespace-pre-wrap" data-testid="script-output">
                              {pollingResult.output}
                            </div>
                          )}
                          {!pollingResult.output && pollingResult.message && !pollingResult.completed && (
                            <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                              {pollingResult.message}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Templates Tab */}
                  {remoteSubTab === 'templates' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">Save script configurations as reusable templates for quick access.</p>
                        <Button size="sm" variant="outline" onClick={() => { setTemplateForm({ name: '', description: '', script_type: 'POWERSHELL', script_content: '', run_as: 'SYSTEM', category: 'general', scope: 'global' }); setEditingTemplateId(null); setShowTemplateForm(true); }} data-testid="new-template-btn">
                          <Plus className="h-4 w-4 mr-1" /> New Template
                        </Button>
                      </div>

                      {showTemplateForm && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="template-form">
                          <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-1">
                            <Bookmark className="h-4 w-4" />
                            {editingTemplateId ? 'Edit Template' : 'New Template'}
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Template name" value={templateForm.name} onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))} data-testid="tpl-name-input" />
                            <Select value={templateForm.category} onValueChange={(v) => setTemplateForm(p => ({ ...p, category: v }))}>
                              <SelectTrigger data-testid="tpl-category-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">General</SelectItem>
                                <SelectItem value="maintenance">Maintenance</SelectItem>
                                <SelectItem value="diagnostics">Diagnostics</SelectItem>
                                <SelectItem value="security">Security</SelectItem>
                                <SelectItem value="network">Network</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Input placeholder="Description (optional)" value={templateForm.description} onChange={(e) => setTemplateForm(p => ({ ...p, description: e.target.value }))} data-testid="tpl-desc-input" />
                          <Textarea className="font-mono text-xs" rows={3} value={templateForm.script_content} onChange={(e) => setTemplateForm(p => ({ ...p, script_content: e.target.value }))} placeholder="Script content or parameters..." data-testid="tpl-script-input" />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveTemplate} data-testid="tpl-save-btn">Save</Button>
                            <Button size="sm" variant="outline" onClick={() => { setShowTemplateForm(false); setEditingTemplateId(null); }}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      {savedTemplates.length === 0 && !showTemplateForm ? (
                        <div className="text-center py-8 text-gray-500">
                          <FolderOpen className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm font-medium">No templates saved yet</p>
                          <p className="text-xs mt-1">Save script configurations for quick reuse</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="templates-list">
                          {savedTemplates.map((tpl) => (
                            <div key={tpl.id} className="p-2.5 bg-gray-50 rounded-lg border hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer group" data-testid={`template-${tpl.id}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0" onClick={() => handleUseTemplate(tpl)}>
                                  <div className="flex items-center gap-1.5">
                                    <Bookmark className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                                    <span className="text-sm font-medium truncate">{tpl.name}</span>
                                  </div>
                                  {tpl.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{tpl.description}</p>}
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="text-[10px] px-1.5">{tpl.script_type}</Badge>
                                    <Badge variant="outline" className="text-[10px] px-1.5 capitalize">{tpl.category}</Badge>
                                    {tpl.scope !== 'global' && <Badge variant="outline" className="text-[10px] px-1.5 bg-amber-50 text-amber-700">Customer-specific</Badge>}
                                    {tpl.use_count > 0 && <span className="text-[10px] text-gray-400">Used {tpl.use_count}x</span>}
                                  </div>
                                </div>
                                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                                  <button onClick={() => { setEditingTemplateId(tpl.id); setTemplateForm({ name: tpl.name, description: tpl.description || '', script_type: tpl.script_type, script_content: tpl.script_content, run_as: tpl.run_as || 'SYSTEM', category: tpl.category || 'general', scope: tpl.scope || 'global' }); setShowTemplateForm(true); }} className="text-gray-400 hover:text-blue-600 p-0.5">
                                    <Edit className="h-3 w-3" />
                                  </button>
                                  <button onClick={() => handleDeleteTemplate(tpl.id)} className="text-gray-400 hover:text-red-500 p-0.5">
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* History Tab */}
                  {remoteSubTab === 'history' && (
                    <div className="space-y-3">
                      {cmdHistory.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <History className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm font-medium">No command history</p>
                          <p className="text-xs mt-1">Commands executed on this device will appear here</p>
                        </div>
                      ) : (
                        <div className="max-h-[400px] overflow-y-auto space-y-2">
                          {cmdHistory.map((entry) => (
                            <div key={entry.id} className="p-2 bg-gray-50 rounded border text-xs" data-testid={`history-${entry.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px] px-1.5">{entry.script_type}</Badge>
                                  <span className={entry.success ? 'text-green-600' : entry.status === 'submitted' ? 'text-blue-600' : 'text-red-600'}>
                                    {entry.status}
                                  </span>
                                  <span className="text-gray-400">{entry.executed_by}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400">{entry.created_at?.slice(0, 16).replace('T', ' ')}</span>
                                  {entry.status === 'submitted' && !pollingHistoryId && (
                                    <button
                                      onClick={async () => {
                                        setPollingHistoryId(entry.id);
                                        setPollingResult({ status: 'pending', message: 'Checking...' });
                                        try {
                                          const resp = await api.get(`/ninja-remote/history/${entry.id}/poll-result`);
                                          setPollingResult(resp.data);
                                          if (resp.data.completed) { setPollingHistoryId(null); loadCommandHistory(); }
                                        } catch { setPollingResult({ status: 'error', message: 'Failed to check' }); setPollingHistoryId(null); }
                                      }}
                                      className="text-blue-500 hover:text-blue-700 ml-1"
                                      title="Check for results"
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </button>
                                  )}
                                  <button onClick={() => handleDeleteHistoryEntry(entry.id)} className="text-gray-400 hover:text-red-500 ml-1">
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                              <pre className="font-mono text-[11px] text-gray-700 bg-white p-1.5 rounded border mt-1 whitespace-pre-wrap break-all">{entry.script_content}</pre>
                              {entry.output && (
                                <div className="mt-1.5 bg-gray-900 text-green-400 p-2 rounded font-mono text-[11px] max-h-[150px] overflow-y-auto whitespace-pre-wrap" data-testid={`history-output-${entry.id}`}>
                                  {entry.output}
                                </div>
                              )}
                              {entry.error && <p className="text-red-500 mt-1">{entry.error}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        <TabsContent value="tickets">
          <Card>
            <CardHeader>
              <CardTitle>Tickets Linked to This Asset</CardTitle>
            </CardHeader>
            <CardContent>
              {relatedTickets.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Ticket className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p>No tickets linked to this asset</p>
                  <p className="text-sm mt-1">Tickets can be linked when creating or editing them</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {relatedTickets.map((ticket) => (
                    <RecordLink
                      key={ticket.id}
                      to={recordHref('ticket', ticket.id)}
                      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                      fallbackClassName="block rounded-lg"
                      data-testid={`asset-ticket-link-${ticket.id}`}
                    >
                      <Card className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-mono text-gray-500">{ticket.ticket_number}</span>
                                <Badge className="text-xs capitalize">{ticket.status}</Badge>
                                <Badge className="text-xs capitalize">{ticket.priority}</Badge>
                              </div>
                              <h4 className="font-semibold">{ticket.title}</h4>
                              {ticket.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{htmlToText(ticket.description, { maxLength: 200 })}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                Created {formatInCST(ticket.created_at, 'MMM d, yyyy h:mm a')}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </RecordLink>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
